import { FormDialogMode, SelectOption, useNotify } from '@globaleris/ui.library'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumTipoDocumento, EnumTipoPeaje } from '../../../api'
import { DocumentModel } from '../../../api/Argentia/endpoints/documents/model/document'
import { ProfileModel } from '../../../api/Argentia/endpoints/profiles/model/profiles'
import { EnumNivelDesglose } from '../../../api/Argentia/enums/enumNivelDesglose'
import { EnumTipoPerfil } from '../../../api/oData/enums/enumTipoPerfil'
import { EnumSubtipoPerfil } from '../../../api/oData/enums/enumSubtipoPerfil'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import PageLeftColumn from '../../../components/page/pageLeftColumn'
import {
  AttachFileDialog,
  AttachFileDialogRef
} from '../../../components/shared/documents/attachFileDialog'
import { ObjectionsDialog } from '../../../components/shared/objectionsDialog/objectionsDialog'
import { HistoricalFilesCard } from '../../../components/shared/profiles/historicalFilesCard/historicalFilesCard'
import {
  enumToSelectOptionArrayDefaultValues,
  enumTranslateOption,
  enumValueToKey
} from '../../../helpers'
import { fileDownloadHelper } from '../../../helpers/fileDownloadHelper'
import { getUserPermissions } from '../../../helpers/permissionsHelper'
import { useConfirmationDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { Pages } from '../../../model/RBAC/constants'
import {
  completeDataTableActions,
  mapDocumentsModelToUploadFileModel
} from '../../documents/documents.mapper'
import {
  downloadDocument,
  downloadProfileTemplate,
  fetchDocumentObjectionsDescriptions,
  fetchObjectionsByDocumentId,
  getDocumentsByProfileId,
  uploadFile
} from '../../documents/documents.service'
import { ObjectionTableModel } from '../../invoicesPage/invoicesPage.Model'
import { mapAttachedDocumentFileItemToDocument } from '../../procedures/procedures.mapper'
import { AttachedDocumentFile } from '../../procedures/procedures.types'
import { ProfileFormData, ProfilesFormDialog } from '../components/profilesFormDialog'
import { mapNewProfileToPatchRequest } from '../profiles.mapper'
import {
  deleteProfileById,
  fetchIsUsedInAnyLead,
  getProfileById,
  updateProfile
} from '../profiles.services'
import { ProfileEditActions } from './components/profileEditActions'
import { ProfileEditLeftPanelDetails } from './components/profilesDetails'
import { getActiveCompany } from '../../../redux'

interface ProfileEditProps {
  location?: {
    state: {
      profileData?: ProfileModel
    }
  }
}

export const ProfileEditPage: React.FC<ProfileEditProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'profiles'])
  const history = useHistory()
  const { info, error } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const [downloadSeasonsTemplate, setDownloadSeasonsTemplate] = useState<boolean>(false)
  const [profileData, setProfileData] = useState<ProfileModel>(props.location?.state.profileData!)
  const [documentData, setDocumentData] = useState<DocumentModel[]>([])
  const profileCommodity = profileData?.commodity
  const profileDocumentTypes: EnumTipoDocumento[] =
    profileCommodity === EnumCommodity.GasNatural
      ? [EnumTipoDocumento.ProfileTemplate]
      : [EnumTipoDocumento.ProfileTemplate, EnumTipoDocumento.PFinalREE]

  const [formDialogOpen, setFormDialogOpen] = useState<boolean>(false)
  const [formMode, setFormMode] = useState<FormDialogMode>(FormDialogMode.EDIT)
  const [formInputData, setFormInputData] = useState<ProfileModel>({} as ProfileModel)

  const [objectionsDialogIsOpen, setObjectionsDialogIsOpen] = useState<boolean>(false)
  const [documentObjections, setDocumentObjections] = useState<ObjectionTableModel[] | undefined>(
    undefined
  )
  const [documentObjectionsDescription, setDocumentObjectionsDescription] = useState<
    ObjectionTableModel[] | undefined
  >(undefined)

  const attachFileDialogRef = useRef<AttachFileDialogRef>(null)
  const [showAttachmentDialog, setShowAttachmentDialog] = useState<boolean>(false)
  const [documentsTypes, setDocumentsTypes] = useState<SelectOption[]>(
    enumToSelectOptionArrayDefaultValues(
      EnumTipoDocumento,
      'enums:EnumTipoDocumento',
      profileDocumentTypes
    )
  )

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isFetchingDocumentData, setIsFetchingDocumentData] = useState<boolean>(false)
  const [canDelete, setCanDelete] = useState<boolean>(
    profileData?.profileType === EnumTipoPerfil.Facturacion
  )
  const [isCheckingDelete, setIsCheckingDelete] = useState<boolean>(false)

  if (!profileData) {
    history.goBack()
    return <></>
  }

  const commodity = profileData.commodity
  const company = getActiveCompany()?.description
  const seasonsTemplateCompany = 'ENERGYA VM'
  // #region Handlers Starts

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.goBack()
  }

  const handleEdit = async () => {
    setFormMode(FormDialogMode.EDIT)
    setFormDialogOpen(true)
    setFormInputData(profileData)
  }

  const handleDelete = async () => {
    await getConfirmation(ConfirmationDialogs.DELETE_PROFILE).then(async () => {
      await deleteProfileById(profileData.id!).then(response => {
        if (response.success) {
          info(t('profiles:components.profilesDataTable.notifications.deleteSuccess'))
          history.push(NonAuthRoutes.Profiles)
        } else {
          error(t('profiles:components.profilesDataTable.notifications.deleteError'))
        }
      })
      closeConfirmation()
    })
  }

  // ------------------- Edit Profile Form Dialog Handlers -----------------------------//
  const handleFormDialogClose = async (formData: ProfileFormData) => {
    if (formData) {
      if (formMode === FormDialogMode.EDIT) {
        const request = mapNewProfileToPatchRequest(formData)
        await updateProfile(request).then(response => {
          if (response.success) {
            info(t('profiles:components.profilesFormDialog.edit.notifications.success'))
            refreshProfileData()
          } else {
            error(t('profiles:components.profilesFormDialog.edit.notifications.error'))
            return
          }
        })
      }
    }
    setFormDialogOpen(false)
  }

  // ------------------- Historic Files/Document Card Handlers -----------------------------//

  const handleAddDocument = async () => {
    handleOpenDialog()
  }

  const handleDownloadFile = async (document: DocumentModel) => {
    if (document.id) {
      await downloadDocument(document.id).then(response => {
        if (response.success && response.data) {
          fileDownloadHelper(response.data.name, response.data)
        } else {
          error(t('documents:notifications.downloadFiles.error'))
        }
      })
    }
  }

  const handleViewObjections = async (document: DocumentModel) => {
    setObjectionsDialogIsOpen(true)
    await fetchObjectionsByDocumentId(document.id!).then(response => {
      if (response.success) {
        setDocumentObjections(response.data)
      }
    })
  }

  // -------------------- Objections Actions -------------------- //
  const handleObjectionsDialogClose = () => {
    setObjectionsDialogIsOpen(false)
    setDocumentObjections(undefined)
  }

  // -------------------- Attach File Dialog Handlers -------------------- //

  const handleCloseDialog = () => {
    onResetDialog()
    setShowAttachmentDialog(false)
  }

  const handleOpenDialog = () => {
    setShowAttachmentDialog(true)
  }

  const handleDownloadSeasonsTemplateClick = () => {
    setDownloadSeasonsTemplate(true)
    handleOpenDialog()
  }
  const handleDownloadGenericTemplateClick = () => {
    setDownloadSeasonsTemplate(false)
    handleOpenDialog()
  }

  const handleSaveDocument = async (document: AttachedDocumentFile, reopen?: boolean) => {
    await mapAttachedDocumentFileItemToDocument(document).then(async document => {
      const request = mapDocumentsModelToUploadFileModel([document], document.type)
      return await uploadFile(request)
        .then(response => {
          if (response.success) {
            info(t('documents:notifications.create.success'))
            fetchDocumentData()
          } else {
            error(t('documents:notifications.create.error'))
          }
        })
        .finally(() => {})
    })
    handleCloseDialog()
    reopen &&
      setTimeout(() => {
        handleOpenDialog()
      }, 700)
  }

  const handleSaveAndNewDocument = (document: AttachedDocumentFile) => {
    handleSaveDocument(document, true)
  }

  const onResetDialog = () => {
    attachFileDialogRef?.current && attachFileDialogRef?.current?.reset()
  }

  const handleDownloadTemplate = async () => {
    await downloadProfileTemplate(profileData.id!, downloadSeasonsTemplate).then(response => {
      if (response.success && response.data) {
        fileDownloadHelper(response.data.name, response.data)
      } else {
        error(t('profiles:fetchErrors.downloadTemplate'))
      }
    })
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const refreshProfileData = async () => {
    setIsFetching(true)
    await getProfileById(profileData.id!)
      .then(response => {
        if (response.success && response.data) {
          setProfileData(response.data)
        } else {
          error(t('profiles:fetchErrors.profileData'))
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  const fetchDocumentData = useCallback(async () => {
    setIsFetchingDocumentData(true)
    await getDocumentsByProfileId(profileData.id!)
      .then(response => {
        if (response.success && response.data) {
          setDocumentData(completeDataTableActions(response.data))
        } else {
          error(t('profiles:fetchErrors.documentData'))
        }
      })
      .finally(() => {
        setIsFetchingDocumentData(false)
      })
  }, [profileData.id])

  useEffect(() => {
    fetchDocumentData()
  }, [])

  useEffect(() => {
    if (profileData?.profileType !== EnumTipoPerfil.Facturacion && profileData?.id) {
      setIsCheckingDelete(true)
      fetchIsUsedInAnyLead(profileData.id)
        .then(response => {
          if (response.success && response.data !== undefined) {
            setCanDelete(!response.data)
          }
        })
        .finally(() => setIsCheckingDelete(false))
    }
  }, [profileData?.id])

  useEffect(() => {
    fetchDocumentObjectionsDescriptions().then(response => {
      if (response.success) {
        setDocumentObjectionsDescription(response.data)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.MULTI_COLUMN} fullWidth={true}>
      <PageLeftColumn>
        <ProfileEditLeftPanelDetails
          isLoading={isFetching}
          code={profileData.code ?? ''}
          description={profileData.description ?? ''}
          profileSubtype={
            profileData.profileSubtype !== undefined
              ? (enumTranslateOption(
                  profileData.profileSubtype,
                  EnumSubtipoPerfil,
                  'enums:EnumSubtipoPerfil'
                ) ?? '')
              : undefined
          }
          breakdownLevel={enumValueToKey(EnumNivelDesglose, profileData.breakdownLevel) ?? ''}
          toll={enumValueToKey(EnumTipoPeaje, profileData.toll) ?? ''}
          commodity={enumTranslateOption(commodity, EnumCommodity, 'enums:EnumCommodity') ?? ''}
        />
      </PageLeftColumn>

      <PageCenterColumn>
        <PageHeader title={t('profiles:header.title')}>
          <ProfileEditActions
            onBackButtonClick={handleBackButtonClick}
            onEditClick={handleEdit}
            onDeleteClick={handleDelete}
            canDelete={canDelete}
            isCheckingDelete={isCheckingDelete}
          />
        </PageHeader>

        <HistoricalFilesCard
          data={documentData}
          isLoading={isFetchingDocumentData}
          onDownloadActionHandler={handleDownloadFile}
          onViewObjectionsHandler={handleViewObjections}
          {...(company === seasonsTemplateCompany && {
            onDownloadSeasonsTemplateClick: handleDownloadSeasonsTemplateClick,
            onDownloadGenericTemplateClick: handleDownloadGenericTemplateClick
          })}
          {...(company !== seasonsTemplateCompany && {
            onDocumentAddClick: handleAddDocument
          })}
          showDownloadCurrentButton
          supplyPointId={profileData.supplyPoint?.id}
        />
      </PageCenterColumn>

      <ProfilesFormDialog
        formMode={formMode}
        open={formDialogOpen}
        onClose={handleFormDialogClose}
        inputData={profileData}
        commodity={profileData.commodity}
        profileType={profileData.profileType}
      />

      <ObjectionsDialog
        open={objectionsDialogIsOpen}
        handleClose={handleObjectionsDialogClose}
        title={t('profiles:components.objectionsDialog.title')}
        objDescriptions={documentObjectionsDescription ?? []}
        objectionsData={documentObjections}
        hasEditPermission={false}
        tableUserPermissions={getUserPermissions(Pages.Profiles)}
      />

      <AttachFileDialog
        prolifeFormConfig
        ref={attachFileDialogRef}
        open={showAttachmentDialog}
        documentTypes={documentsTypes}
        onClose={handleCloseDialog}
        onSave={handleSaveDocument}
        onSaveAndNew={handleSaveAndNewDocument}
        ondownloadTemplate={handleDownloadTemplate}
      />
    </Page>
  )
}
